'use client';

import { useEffect, useState } from 'react';
import { CheckCircle2, Info, AlertTriangle, X } from 'lucide-react';
import {
  usePageBannerState,
  usePageBannerDismiss,
  type PageBannerState,
  type PageBannerTone,
} from './page-banner-context';

const TONE_STYLES: Record<PageBannerTone, { wrap: string; icon: string; close: string; Icon: typeof CheckCircle2 }> = {
  success: {
    wrap: 'bg-emerald-50/95 border-emerald-200/80 text-emerald-800 dark:bg-emerald-950/50 dark:border-emerald-900/70 dark:text-emerald-200',
    icon: 'text-emerald-600 dark:text-emerald-400',
    close: 'hover:bg-emerald-100/80 text-emerald-700 dark:hover:bg-emerald-900/40 dark:text-emerald-300',
    Icon: CheckCircle2,
  },
  info: {
    wrap: 'bg-sky-50/95 border-sky-200/80 text-sky-800 dark:bg-sky-950/50 dark:border-sky-900/70 dark:text-sky-200',
    icon: 'text-sky-600 dark:text-sky-400',
    close: 'hover:bg-sky-100/80 text-sky-700 dark:hover:bg-sky-900/40 dark:text-sky-300',
    Icon: Info,
  },
  warning: {
    wrap: 'bg-amber-50/95 border-amber-200/80 text-amber-900 dark:bg-amber-950/50 dark:border-amber-900/70 dark:text-amber-200',
    icon: 'text-amber-600 dark:text-amber-400',
    close: 'hover:bg-amber-100/80 text-amber-700 dark:hover:bg-amber-900/40 dark:text-amber-300',
    Icon: AlertTriangle,
  },
  danger: {
    wrap: 'bg-rose-50/95 border-rose-200/80 text-rose-900 dark:bg-rose-950/50 dark:border-rose-900/70 dark:text-rose-200',
    icon: 'text-rose-600 dark:text-rose-400',
    close: 'hover:bg-rose-100/80 text-rose-700 dark:hover:bg-rose-900/40 dark:text-rose-300',
    Icon: AlertTriangle,
  },
};

/**
 * Overlay vizual pentru banner-ul de pagină. Slide-in din dreapta peste
 * secțiunea companiei din TopBar, auto-dismiss după `duration` ms.
 *
 * Montat ca ultim child în `<header>`-ul TopBar — folosește `position: absolute`
 * ca să nu afecteze layout-ul (zero redimensionare a sectiunilor TopBar).
 *
 * Animație entry: 240ms cubic-bezier(0.32, 0.72, 0, 1) (spring) — translate-x-full → 0
 * Animație exit: 200ms ease-in — translate-x-0 → translate-x-full
 */
export function PageBannerOverlay() {
  const banner = usePageBannerState();
  const dismiss = usePageBannerDismiss();

  // `mounted` separates "banner state non-null" from "should be visible (post-RAF)".
  // We want the element rendered with `translate-x-full` first, then on next frame
  // toggle to `translate-x-0` so the CSS transition fires.
  const [mounted, setMounted] = useState<PageBannerState | null>(null);
  const [visible, setVisible] = useState(false);

  // Sync banner state into mounted; trigger slide-in via RAF after mount.
  useEffect(() => {
    if (banner) {
      setMounted(banner);
      const raf = requestAnimationFrame(() => setVisible(true));
      return () => cancelAnimationFrame(raf);
    } else if (mounted) {
      // Banner cleared externally — slide out, then unmount after transition
      setVisible(false);
      const t = setTimeout(() => setMounted(null), 220);
      return () => clearTimeout(t);
    }
  }, [banner, mounted]);

  // Auto-dismiss după duration; reset la schimbarea ID
  useEffect(() => {
    if (!banner) return;
    const t = setTimeout(() => dismiss(), banner.duration);
    return () => clearTimeout(t);
  }, [banner?.id, banner?.duration, dismiss]);

  if (!mounted) return null;

  const t = TONE_STYLES[mounted.tone] || TONE_STYLES.success;
  const { Icon } = t;

  return (
    <div
      role="status"
      aria-live="polite"
      title={mounted.label}
      className={
        'absolute right-4 sm:right-6 top-1/2 -translate-y-1/2 z-[51] ' +
        'flex items-start gap-2.5 min-h-[40px] min-w-[240px] max-w-[min(520px,calc(100vw-32px))] py-2 px-3.5 ' +
        'rounded-2xl border shadow-sm backdrop-blur-md text-[0.8125rem] font-medium leading-snug ' +
        'transition-[transform,opacity] duration-[240ms] ease-[cubic-bezier(0.32,0.72,0,1)] ' +
        (visible ? 'translate-x-0 opacity-100 ' : 'translate-x-[120%] opacity-0 ') +
        t.wrap
      }
    >
      <Icon className={'h-4 w-4 shrink-0 mt-0.5 ' + t.icon} aria-hidden="true" />
      <span className="flex-1 line-clamp-2 break-words">{mounted.label}</span>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Închide notificarea"
        className={
          'h-5 w-5 -mr-1 mt-0.5 shrink-0 grid place-items-center rounded transition-colors ' + t.close
        }
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  );
}
